import { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, CheckCircle, XCircle, Zap } from 'lucide-react';
import { useEngine, useConnection } from '../context/MarketContext';
import type { QuoteAction } from '../types';

const ACTION_STYLES: Record<QuoteAction, { color: string; bg: string; label: string }> = {
  NEW:    { color: '#4da3ff', bg: '#4da3ff15', label: 'NEW' },
  UPDATE: { color: '#ff8c3a', bg: '#ff8c3a15', label: 'UPDATE' },
  CANCEL: { color: '#ff5c5c', bg: '#ff5c5c15', label: 'CANCEL' },
  HOLD:   { color: '#7a8693', bg: '#7a869315', label: 'HOLD' },
};

function getConfidenceColor(c: number) {
  return c >= 0.7 ? '#36c98e' : c >= 0.4 ? '#ff8c3a' : '#ff5c5c';
}

export default function FloatingQuoteSuggestion() {
  const { quoteSuggestions, sendQuoteDecision } = useEngine();
  const { connectionStatus } = useConnection();
  const [dismissed, setDismissed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevQuoteIdRef = useRef<string | null>(null);

  // Show the latest quote from any symbol
  const quote = quoteSuggestions[0];

  // Reset dismissed state and restart timer whenever a new quote arrives
  useEffect(() => {
    if (!quote) return;
    if (quote.quote_id !== prevQuoteIdRef.current) {
      prevQuoteIdRef.current = quote.quote_id;
      setDismissed(false);
      setProcessing(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      // Automated mode: auto-dismiss faster (4s), manual: 10s
      const ttl = quote.mode === 'automated' ? 4000 : 10000;
      timerRef.current = setTimeout(() => setDismissed(true), ttl);
    }
  }, [quote?.quote_id]);

  // Cleanup on unmount
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (!quote || dismissed) return null;

  const isAutomated = quote.mode === 'automated';
  const hasBid = quote.bid.price !== null && quote.bid.qty !== null;
  const hasAsk = quote.ask.price !== null && quote.ask.qty !== null;
  const confidenceColor = getConfidenceColor(quote.confidence);
  const actionStyle = quote.action ? ACTION_STYLES[quote.action] : null;
  const isConnected = connectionStatus === 'connected';

  const handleDecision = (accepted: boolean) => {
    if (processing || !isConnected) return;
    setProcessing(true);
    const ok = sendQuoteDecision(quote.quote_id, accepted, accepted ? 'Trader approved' : 'Trader rejected');
    if (!ok) {
      setProcessing(false);
      return;
    }
    setDismissed(true);
  };

  // Automated mode: accent color is purple/violet to distinguish from manual (blue/green)
  const borderColor = isAutomated ? '#c77dff' : confidenceColor;

  return (
    <>
      <div style={{
        position: 'fixed', bottom: 87, right: 88, zIndex: 8999,
        width: 260,
        backgroundColor: '#10151b',
        border: `1px solid ${borderColor}35`,
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: 10,
        boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
        padding: '10px 12px',
        display: 'flex', flexDirection: 'column', gap: 8,
        animation: 'quoteSlideIn 0.2s ease-out',
        opacity: processing ? 0.6 : 1,
      }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Mode badge */}
          {isAutomated ? (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
              color: '#c77dff', backgroundColor: '#c77dff15',
              border: '1px solid #c77dff40',
              borderRadius: 3, padding: '1px 5px', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 3,
            }}>
              <Zap size={8} /> AUTO
            </span>
          ) : null}
          {/* Symbol chip */}
          <span style={{
            fontSize: 10, fontWeight: 700, color: '#7e8a96',
            backgroundColor: '#0a0d10', border: '1px solid #2a3540',
            borderRadius: 4, padding: '1px 6px', flexShrink: 0,
          }}>
            {quote.ticker || quote.symbol || quote.secId}
          </span>
          {/* Action badge */}
          {actionStyle && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
              color: actionStyle.color, backgroundColor: actionStyle.bg,
              border: `1px solid ${actionStyle.color}40`,
              borderRadius: 3, padding: '1px 5px', flexShrink: 0,
            }}>
              {actionStyle.label}
            </span>
          )}
          {/* Confidence */}
          <span style={{
            fontSize: 9, fontWeight: 600, color: confidenceColor,
            backgroundColor: `${confidenceColor}15`, border: `1px solid ${confidenceColor}30`,
            borderRadius: 3, padding: '1px 5px', marginLeft: 'auto', flexShrink: 0,
          }}>
            {(quote.confidence * 100).toFixed(0)}%
          </span>
          {/* Dismiss button */}
          <button
            onClick={() => setDismissed(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#46525e', fontSize: 14, padding: 0, lineHeight: 1, flexShrink: 0 }}
          >
            ×
          </button>
        </div>

        {/* Bid / Ask compact grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {hasBid ? (
            <div style={{ backgroundColor: '#36c98e12', border: '1px solid #36c98e25', borderRadius: 5, padding: '5px 7px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: '#36c98e', fontWeight: 700, marginBottom: 2 }}>
                <TrendingUp size={9} /> BID
              </div>
              <div style={{ fontSize: 13, color: '#d7dde4', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {quote.bid.price?.toFixed(2)}
              </div>
              <div style={{ fontSize: 10, color: '#7a8693' }}>×{quote.bid.qty?.toLocaleString()}</div>
            </div>
          ) : (
            <div style={{ backgroundColor: '#0a0d10', border: '1px solid #10151b', borderRadius: 5, padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 10, color: '#46525e' }}>No bid</span>
            </div>
          )}
          {hasAsk ? (
            <div style={{ backgroundColor: '#ff5c5c12', border: '1px solid #ff5c5c25', borderRadius: 5, padding: '5px 7px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: '#ff5c5c', fontWeight: 700, marginBottom: 2 }}>
                <TrendingDown size={9} /> ASK
              </div>
              <div style={{ fontSize: 13, color: '#d7dde4', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {quote.ask.price?.toFixed(2)}
              </div>
              <div style={{ fontSize: 10, color: '#7a8693' }}>×{quote.ask.qty?.toLocaleString()}</div>
            </div>
          ) : (
            <div style={{ backgroundColor: '#0a0d10', border: '1px solid #10151b', borderRadius: 5, padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 10, color: '#46525e' }}>No ask</span>
            </div>
          )}
        </div>

        {/* Reason — 1 line truncated */}
        {quote.reason && (
          <div style={{
            fontSize: 10, color: '#7e8a96',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {quote.reason}
          </div>
        )}

        {/* Automated: execution notice | Manual: Accept / Reject buttons */}
        {isAutomated ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 8px', borderRadius: 5,
            backgroundColor: '#c77dff15', border: '1px solid #c77dff30',
          }}>
            <Zap size={11} color="#c77dff" />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#c77dff' }}>
              Executed automatically
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => handleDecision(true)}
              disabled={processing || !isConnected}
              style={{
                flex: 1, padding: '7px 0', fontSize: 11, fontWeight: 700,
                backgroundColor: processing || !isConnected ? '#10151b' : '#36c98e',
                color: processing || !isConnected ? '#46525e' : 'white',
                border: `1px solid ${processing || !isConnected ? '#2a3540' : '#36c98e'}`,
                borderRadius: 5, cursor: processing || !isConnected ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <CheckCircle size={12} /> Accept
            </button>
            <button
              onClick={() => handleDecision(false)}
              disabled={processing || !isConnected}
              style={{
                flex: 1, padding: '7px 0', fontSize: 11, fontWeight: 700,
                backgroundColor: 'transparent',
                color: processing || !isConnected ? '#46525e' : '#ff5c5c',
                border: `1px solid ${processing || !isConnected ? '#2a3540' : '#ff5c5c40'}`,
                borderRadius: 5, cursor: processing || !isConnected ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <XCircle size={12} /> Reject
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes quoteSlideIn {
          from { opacity: 0; transform: translateX(16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
